import { Component, computed, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { LucideArrowLeft, LucideDynamicIcon } from '@lucide/angular';
import { breadrums } from '../../model/breadcrum.model';
import { matTourSteps } from '../../model/matTour.model';
import { TabConfig, TabGroupConfig } from '../../model/form.model';

@Component({
  selector: 'app-breadcrums',
  standalone: true,
  imports: [LucideDynamicIcon],
  templateUrl: './breadcrums.component.html',
  styleUrl: './breadcrums.component.scss'
})
export class BreadcrumsComponent implements OnChanges {
  @Input() breadcrumList: Array<breadrums> = []
  @Input() showBreadcrum = true;
  @Input() tabHeaders: Array<TabConfig> = [];
  @Input() onTabChange: (tab: TabConfig) => void = () => undefined;
  @Input() groups: TabGroupConfig[] = [];
  @Input() selectedGroupIndex = 0;
  @Input() onGroupChange: (index: number) => void = () => undefined;
  @Input() breadcrumbProps?: any;
  protected readonly backIcon = computed(() => LucideArrowLeft);
  matchedTourContent = '';
  activeTab: TabConfig | null = null;

  constructor(private readonly router: Router) { }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateMatchedTourContent();
    if (changes['tabHeaders'] && this.tabHeaders?.length > 0 && !this.activeTab) {
      this.activeTab = this.tabHeaders[0];
    }
  }

  private updateMatchedTourContent(): void {
    const currentScreenName = this.breadcrumList?.at(-1)?.name ?? '';
    const normalizedScreenName = this.normalizeValue(currentScreenName);
    const routeCandidates = this.getRouteCandidates();
    const lookupCandidates = [normalizedScreenName, ...routeCandidates].filter((value) => value.length > 0);

    if (lookupCandidates.length === 0) {
      this.matchedTourContent = '';
      return;
    }

    const exactMatch = matTourSteps.find((step) => {
      const title = this.normalizeValue(step?.title ?? '');
      const anchorId = this.normalizeValue(String(step?.anchorId ?? ''));

      return lookupCandidates.some((candidate) => title === candidate || anchorId === candidate);
    });
    const matchedStep = exactMatch ?? matTourSteps.find((step) => {
      const title = this.normalizeValue(step?.title ?? '');
      const anchorId = this.normalizeValue(String(step?.anchorId ?? ''));

      return lookupCandidates.some((candidate) => (
        (title.length > 0 && title.includes(candidate)) ||
        (anchorId.length > 0 && anchorId.includes(candidate)) ||
        (title.length > 0 && candidate.includes(title)) ||
        (anchorId.length > 0 && candidate.includes(anchorId))
      ));
    });

    this.matchedTourContent = String(matchedStep?.content ?? '');
  }

  private getRouteCandidates(): string[] {
    const path = this.router.url.split('?')[0].split('#')[0];
    return path
      .split('/')
      .map((segment) => this.normalizeValue(segment))
      .filter((segment) => segment.length > 0);
  }

  private normalizeValue(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]/g, ''); 
  }


  setTab(tab: TabConfig): void {
    this.activeTab = tab;
    this.onTabChange(tab);
  }

  selectGroup(index: number): void {
    this.onGroupChange(index);
  }

  handleBackClick(): void {
    const callback = this.breadcrumbProps?.backClicked;
    if (typeof callback === 'function') {
      callback();
    }
  }
}
